/**
 * Companion Tool Registry — COPILOT_MASTER_PLAN subsystem C (unified tool framework).
 *
 * ONE typed registry every Companion surface + the agent-runner share. It WRAPS
 * the existing executors (action-bridge, tool-executor, agent-runner recipes,
 * ml-tools.service) — it does not replace them — so the four op vocabularies stop
 * drifting behind a single contract.
 *
 * HITL policy (subsystem H, mirrored client-side / defence-in-depth):
 *   Assistant mode → every WRITE/DESTRUCTIVE tool requires a HITL confirm.
 *   Agent mode     → READ auto; WRITE auto (with a brief confirmable toast);
 *                    DESTRUCTIVE always HITL.
 *   A tool with `hitl:true` ALWAYS confirms regardless of mode (hard-opposable).
 * The tool implementations that mutate already route through the shared HITL gate
 * (`hitlBackendTool`/`hitlProposeValues`) — the registry adds the mode-level
 * gating + the agent-mode write toast on top, so nothing double-prompts unless
 * the tool itself owns its diff.
 */

import { toast } from 'sonner'
import { CORE_TOOLS } from './tools/core-tools'
import { ALL_ML_CV_TOOLS } from './tools/ml-tools'
import type {
  CompanionTool,
  ToolContext,
  ToolRunResult,
  ToolMode,
  CompanionMode,
} from './tools/types'

export type { CompanionTool, ToolContext, ToolRunResult, ToolMode, CompanionMode } from './tools/types'
export type { ToolCard, ToolArgSpec } from './tools/types'

// ── Registry ──────────────────────────────────────────────────────────────────

const _registry = new Map<string, CompanionTool>()

function register(tool: CompanionTool): void {
  _registry.set(tool.name, tool)
}

// Register core verbs + ML/CV tools once at module load.
for (const t of [...CORE_TOOLS, ...ALL_ML_CV_TOOLS]) register(t)

// Prime the agent-runner's side-effect cache so its read/write step
// classification (skip-on-read-fail vs halt-on-write-fail) covers registry tools
// too — done via a static import registry→agent-runner (agent-runner only
// imports the registry dynamically, so there is no static cycle).
import { primeToolModeCache } from './agent-runner'
primeToolModeCache(
  Object.fromEntries([..._registry.values()].map((t) => [t.name, t.mode])),
)

/** Register (or override) a tool at runtime — cabinet/mobile/experimental. */
export function registerTool(tool: CompanionTool): void {
  register(tool)
}

/** Look up a tool by name. */
export function getTool(name: string): CompanionTool | undefined {
  return _registry.get(name)
}

/** True when a tool with this name is registered. */
export function hasTool(name: string): boolean {
  return _registry.has(name)
}

/** All registered tools (for the palette / planner tool list). */
export function listTools(): CompanionTool[] {
  return [..._registry.values()]
}

/** Tool names, for the planner's `available_tools`. */
export function listToolNames(): string[] {
  return [..._registry.keys()]
}

// ── Alias map: planner/legacy op names → canonical tool names ─────────────────
//
// The F8 planner + chat action markers emit a handful of op names that predate
// the registry (createRecord/linkRecords are handled directly by the runner).
// This maps the newer verbs + a few synonyms onto canonical tools so a plan
// step / chat tool marker resolves.

const OP_ALIASES: Record<string, string> = {
  goTo: 'navigate',
  open: 'openForm',
  create: 'createRecord',
  update: 'updateRecord',
  edit: 'updateRecord',
  query: 'queryData',
  ask: 'queryData',
  run: 'startWorkflow',
  background: 'startWorkflow',
  schedule: 'startWorkflow',
  stop: 'stopWorkflow',
  cancel: 'stopWorkflow',
  tasks: 'recallTasks',
  alert: 'notify',
  message: 'notify',
  analyze: 'analyzeCurrentPage',
}

/** Resolve an op name (canonical or alias) to a registered tool. */
export function resolveTool(op: string): CompanionTool | undefined {
  return _registry.get(op) ?? _registry.get(OP_ALIASES[op] ?? '')
}

// ── HITL policy per mode (subsystem H) ────────────────────────────────────────

export type GateDecision = 'auto' | 'toast-confirm' | 'hitl'

/**
 * Decide the gating for a tool given the mode. This is the single place the
 * Assistant/Agent policy lives client-side (mirrors the server allow-list).
 */
export function gateFor(mode: ToolMode, hitl: boolean, companionMode: CompanionMode): GateDecision {
  if (hitl) return 'hitl' // hard-opposable / tool owns its own diff → always confirm
  if (mode === 'read') return 'auto'
  if (mode === 'destructive') return 'hitl' // destructive ALWAYS confirms, any mode
  // write:
  if (companionMode === 'assistant') return 'hitl'
  return 'toast-confirm' // agent mode → auto, but a brief confirmable toast
}

/** Await a lightweight confirm toast (agent-mode write). Resolves true on confirm. */
function awaitToastConfirm(label: string): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false
    const done = (v: boolean) => {
      if (settled) return
      settled = true
      resolve(v)
    }
    const id = toast(label, {
      duration: 6000,
      action: { label: 'Confirmer', onClick: () => done(true) },
      cancel: { label: 'Annuler', onClick: () => done(false) },
      onDismiss: () => done(true), // auto-proceed on timeout in agent mode
      onAutoClose: () => done(true),
    })
    // Safety net in case the toast lib never fires a lifecycle callback.
    setTimeout(() => done(true), 6500)
    void id
  })
}

// ── Dispatch ──────────────────────────────────────────────────────────────────

/**
 * Invoke a tool by name (or alias), applying the mode-level HITL policy on top of
 * whatever gate the tool implementation already owns.
 *
 *  • read            → runs immediately.
 *  • write + agent   → brief confirmable toast, then runs (tool may still show its own diff).
 *  • write + assistant OR destructive OR hitl:true → the tool's OWN HITL gate is
 *    the authoritative confirm; for tools without their own diff we still surface
 *    a toast-confirm so nothing silently mutates.
 *
 * Returns a ToolRunResult; `cancelled:true` when the user declined a gate.
 */
export async function dispatchTool(
  op: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Promise<ToolRunResult> {
  const tool = resolveTool(op)
  if (!tool) {
    return { ok: false, detail: `Outil inconnu : « ${op} »` }
  }

  // Validate required args (light — the tools are defensive too).
  const missing = tool.argsSchema
    .filter((a) => a.required && (args[a.name] === undefined || args[a.name] === null || args[a.name] === ''))
    .map((a) => a.name)
  if (missing.length > 0) {
    return { ok: false, detail: `« ${tool.name} » : argument(s) manquant(s) : ${missing.join(', ')}` }
  }

  const decision = gateFor(tool.mode, tool.hitl, ctx.mode)

  // Tools that own their own HITL diff (fillForm, createRecord, updateRecord,
  // stopWorkflow) carry `hitl:true` OR mode==='destructive'|write-in-assistant.
  // For those we let the tool's internal gate be authoritative and DON'T add a
  // second prompt. For write-in-agent (toast-confirm) on a tool WITHOUT its own
  // diff (e.g. notify), we surface the brief toast.
  if (decision === 'toast-confirm' && !tool.hitl) {
    const proceed = await awaitToastConfirm(`Exécuter « ${tool.description} » ?`)
    if (!proceed) return { ok: false, cancelled: true, detail: 'Annulé' }
  }

  try {
    return await tool.run(args, ctx)
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Erreur inconnue'
    return { ok: false, detail: `« ${tool.name} » a échoué : ${msg}` }
  }
}

// ── Page-context ML tool proposals (subsystem N) ──────────────────────────────

/**
 * Which ML/CV tools are RELEVANT to propose on the current page, from the module
 * / entity type in context. Powers the "Lancer une prédiction ML sur ce …"
 * suggestion chips in the Companion window.
 */
export function proposeToolsForContext(ctx: ToolContext): CompanionTool[] {
  const entityType = (ctx.entity?.entityType ?? '').toLowerCase()
  const module = (ctx.module ?? '').toLowerCase()
  const key = `${entityType} ${module}`

  const matches = (names: string[]) => names.map((n) => _registry.get(n)).filter(Boolean) as CompanionTool[]

  if (/risk|risque|kri/.test(key)) return matches(['predictRisk', 'detectAnomalies', 'forecast'])
  if (/control|contrôle|controle/.test(key)) return matches(['scoreControlEffectiveness', 'predictControlFailure'])
  if (/vendor|fournisseur|tiers/.test(key)) return matches(['predictVendorRisk'])
  if (/compliance|conformit|obligation|framework|cadre/.test(key))
    return matches(['predictGap', 'predictComplianceDrift', 'forecast'])
  if (/incident|security|sécur|secur/.test(key)) return matches(['incidentSeverity', 'detectAnomalies'])
  // Default: the broadly-applicable advisory tools.
  return matches(['detectAnomalies', 'forecast', 'queryData'])
}

// ── window bridge (headed-Chromium verification) ──────────────────────────────

declare global {
  interface Window {
    sgrcCopilotRegistry?: {
      dispatch: (op: string, args: Record<string, unknown>, ctx: ToolContext) => Promise<ToolRunResult>
      list: () => string[]
      has: (name: string) => boolean
      gateFor: (mode: ToolMode, hitl: boolean, companionMode: CompanionMode) => GateDecision
    }
  }
}

export function mountToolRegistryBridge(): void {
  if (typeof window === 'undefined') return
  window.sgrcCopilotRegistry = {
    dispatch: dispatchTool,
    list: listToolNames,
    has: hasTool,
    gateFor,
  }
}
